import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Dropdown, Grid, Progress, Row, Segmented, Space, Switch, Table, Tag, Typography, Upload } from 'antd';
import {
  CameraOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  InboxOutlined,
  MoreOutlined,
  ReloadOutlined,
  RightOutlined,
  ScanOutlined,
  SendOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { CardAudience, CardScanItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { NameCell } from '../../components/NameCell';
import { CardTitle, StatCard } from '../../components/StatCard';
import { tableLoading, tableLocale } from '../../components/states';
import { errorMessage, fileUrl } from '../../api/client';
import { palette } from '../../app/theme';
import { fmtDateTime, plural } from '../../utils/format';
import { useLists, useSegments, useTags } from '../../features/audience/api';
import { useAddedByTag, useCardScans, useCardScanSummary, useDiscardCardScan, useRetryCardScan, useSaveManyCardScans, useUploadCardScan } from '../../features/cardScans/api';
import { prepareCardPhoto } from '../../features/cardScans/image';
import { EventPicker, NO_EVENT, rememberAudience, rememberedAudience, rememberedEvent, rememberEvent } from './EventPicker';
import { CardAudienceFields, useKnownAudience } from './CardAudienceFields';

export const SCAN_PATH = '/tools/scan-cards';

type Pending = { front: Blob; frontUrl: string; qrText: string | null };
type View = 'open' | 'saved';

const FRONT_ONLY_KEY = 'crm.cardScan.frontOnly';
const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeFlag = (key: string, on: boolean) => {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    // private window: the choice lasts for this visit only
  }
};

export const cardName = (s: CardScanItem) => {
  const d = s.extracted;
  const name = [d?.firstName, d?.lastName].filter(Boolean).join(' ');
  return name || d?.company || d?.emails[0]?.email || 'Unnamed card';
};

/** Photos in the order they were taken (then by name), so front/back pairs line up. */
const byTaken = (files: File[]) => [...files].sort((a, b) => a.lastModified - b.lastModified || a.name.localeCompare(b.name, undefined, { numeric: true }));

function StatusTag({ s }: { s: CardScanItem }) {
  if (s.status === 'PROCESSING') return <Tag icon={<SyncOutlined spin />}>Reading</Tag>;
  if (s.status === 'FAILED') return <Tag color="error">Couldn’t read</Tag>;
  if (s.status === 'SAVED') return <Tag color="success">Saved</Tag>;
  if (s.extracted?.uncertain.length) return <Tag color="warning">Hard to read</Tag>;
  return <Tag color="processing">To check</Tag>;
}

/**
 * Tools → Scan cards: photograph cards one after another (or upload a pile of photos); they are read in the
 * background and wait here to be checked one by one, or saved in bulk. Where the cards were collected and the
 * tags, lists and segments picked here go with every card scanned next.
 */
export function ScanCardsPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const phone = Grid.useBreakpoint().md === false;
  const [view, setView] = useState<View>('open');
  const scans = useCardScans('open');
  const saved = useCardScans('saved', view === 'saved');
  const summary = useCardScanSummary(true);
  const addedBy = useAddedByTag();
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const upload = useUploadCardScan();
  const retry = useRetryCardScan();
  const discard = useDiscardCardScan();
  const saveMany = useSaveManyCardScans();
  const [eventTagId, setEventTagId] = useState(rememberedEvent);
  const [picked, setPicked] = useState<CardAudience>(rememberedAudience);
  const audience = useKnownAudience(picked);
  const [frontOnly, setFrontOnly] = useState(() => readFlag(FRONT_ONLY_KEY));
  const [pairs, setPairs] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState<{ done: number; total: number } | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const frontInput = useRef<HTMLInputElement>(null);
  const backInput = useRef<HTMLInputElement>(null);

  const chooseEvent = (id: string) => {
    setEventTagId(id);
    rememberEvent(id);
  };
  const chooseAudience = (a: CardAudience) => {
    setPicked(a);
    rememberAudience(a);
  };

  const send = (front: Blob, back: Blob | null, qrText: string | null, quiet = false) =>
    upload
      .mutateAsync({ front, back, eventTagId: eventTagId || null, qrText, ...audience })
      .then(() => {
        if (!quiet) message.success({ content: qrText ? 'Card sent, QR code found' : 'Card sent, reading it now', key: 'card-sent', duration: 1.5 });
      })
      .catch((e) => {
        message.error(errorMessage(e));
        throw e;
      });

  const onFront = async (file: File | undefined) => {
    if (!file) return;
    setPreparing(true);
    try {
      const { photo, qrText } = await prepareCardPhoto(file);
      if (frontOnly) {
        await send(photo, null, qrText).catch(() => undefined);
        // straight back to the camera for the next card in the pile
        if (phone) frontInput.current?.click();
      } else {
        setPending({ front: photo, frontUrl: URL.createObjectURL(photo), qrText });
      }
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setPreparing(false);
    }
  };

  const finish = async (backFile?: File) => {
    if (!pending) return;
    let back: Blob | null = null;
    let qrText = pending.qrText;
    if (backFile) {
      setPreparing(true);
      try {
        const prepared = await prepareCardPhoto(backFile);
        back = prepared.photo;
        qrText = qrText ?? prepared.qrText;
      } catch (e) {
        message.error((e as Error).message);
      } finally {
        setPreparing(false);
      }
    }
    URL.revokeObjectURL(pending.frontUrl);
    setPending(null);
    void send(pending.front, back, qrText).catch(() => undefined);
  };

  /** Many photos at once: each is the front of its own card, or with `pairs` every two make one card (front, back). */
  const onPhotos = async (files: File[]) => {
    if (!files.length) return;
    const ordered = byTaken(files);
    const cards: [File, File | null][] = pairs ? Array.from({ length: Math.ceil(ordered.length / 2) }, (_, i) => [ordered[i * 2], ordered[i * 2 + 1] ?? null]) : ordered.map((f) => [f, null]);
    setSending({ done: 0, total: cards.length });
    let sent = 0;
    for (const [i, [front, back]] of cards.entries()) {
      try {
        const f = await prepareCardPhoto(front);
        const b = back ? await prepareCardPhoto(back) : null;
        await send(f.photo, b?.photo ?? null, f.qrText ?? b?.qrText ?? null, true);
        sent++;
      } catch (e) {
        if (!(e && typeof e === 'object' && 'isAxiosError' in e)) message.error(`${front.name}: ${(e as Error).message}`);
      }
      setSending({ done: i + 1, total: cards.length });
    }
    setSending(null);
    if (sent) message.success(`${plural(sent, 'card')} sent, reading them now`);
  };

  const all = scans.data ?? [];
  const ready = all.filter((s) => s.status === 'READY');
  const reading = all.filter((s) => s.status === 'PROCESSING');
  const rows = view === 'saved' ? (saved.data ?? []) : all;
  const selectedReady = ready.filter((s) => selected.includes(s.id));
  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tags.data ?? []) m.set(t.id, t.path ?? t.name);
    for (const l of lists.data ?? []) m.set(l.id, l.name);
    for (const s of segments.data ?? []) m.set(s.id, s.name);
    return m;
  }, [tags.data, lists.data, segments.data]);

  const runSaveMany = (target: CardScanItem[]) => {
    if (!target.length) return;
    const unsure = target.filter((s) => s.extracted?.uncertain.length).length;
    modal.confirm({
      title: `Save ${plural(target.length, 'card')} as read?`,
      content: (
        <div>
          <p>Each card is added to the contact with the same email or phone number (filling in what it is missing), or saved as a new contact. Its tags, lists, segments and where you met them go with it.</p>
          {unsure > 0 && <p style={{ marginBottom: 0, color: palette.warning }}>{plural(unsure, 'card')} had details that were hard to read. Check those one by one if you want to be sure.</p>}
        </div>
      ),
      okText: `Save ${plural(target.length, 'card')}`,
      onOk: async () => {
        try {
          const r = await saveMany.mutateAsync(target.map((s) => s.id));
          setSelected([]);
          const done = [r.created && `${plural(r.created, 'new contact')} saved`, r.updated && `${plural(r.updated, 'contact')} updated`].filter(Boolean).join(', ');
          if (done) message.success(done);
          if (r.failed.length) {
            // dialogs render outside the router, so links navigate by hand
            const dialog = modal.warning({
              title: `${plural(r.failed.length, 'card')} not saved`,
              content: (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {r.failed.map((f) => {
                    const s = all.find((x) => x.id === f.id);
                    return (
                      <li key={f.id}>
                        <a
                          onClick={() => {
                            dialog.destroy();
                            navigate(`${SCAN_PATH}/${f.id}`);
                          }}
                        >
                          {s ? cardName(s) : 'Card'}
                        </a>
                        : {f.error}
                      </li>
                    );
                  })}
                </ul>
              ),
            });
          }
        } catch (e) {
          message.error(errorMessage(e));
        }
      },
    });
  };

  const runDiscard = (target: CardScanItem[]) =>
    modal.confirm({
      title: `Discard ${plural(target.length, 'card')}?`,
      content: 'Discarded cards are not saved as contacts.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: async () => {
        for (const s of target) await discard.mutateAsync(s.id).catch((e) => message.error(errorMessage(e)));
        setSelected([]);
        message.info(`${plural(target.length, 'card')} discarded`);
      },
    });

  const goTo = (s: CardScanItem) => (s.status === 'SAVED' && s.contactId ? `/contacts/${s.contactId}` : `${SCAN_PATH}/${s.id}`);
  const busy = preparing || !!sending;

  return (
    <div className="page">
      <PageHeader
        title="Scan cards"
        subtitle="Photograph business cards or upload photos of them, check what was read, and save them as contacts."
        extra={
          !phone && ready.length > 0 && (
            <Button type="primary" icon={<RightOutlined />} onClick={() => navigate(`${SCAN_PATH}/${ready[0].id}`)}>
              Check {ready.length === 1 ? 'the card' : `${ready.length} cards`}
            </Button>
          )
        }
      />
      {/* hidden pickers: capture opens the back camera on phones */}
      <input ref={frontInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void onFront(e.target.files?.[0]).finally(() => (e.target.value = ''))} />
      <input ref={backInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void finish(e.target.files?.[0]).finally(() => (e.target.value = ''))} />

      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {!phone && (
          <Row gutter={[16, 16]}>
            <Col xs={12} xl={6}>
              <StatCard icon={<EyeOutlined />} label="To check" value={summary.data?.ready ?? 0} note="read and waiting for you" loading={summary.isLoading} />
            </Col>
            <Col xs={12} xl={6}>
              <StatCard icon={<ClockCircleOutlined />} label="Being read" value={summary.data?.processing ?? 0} note="read in the background" loading={summary.isLoading} />
            </Col>
            <Col xs={12} xl={6}>
              <StatCard icon={<ExclamationCircleOutlined />} label="Couldn’t read" value={summary.data?.failed ?? 0} note="read again or type them in" loading={summary.isLoading} />
            </Col>
            <Col xs={12} xl={6}>
              <StatCard icon={<UserOutlined />} label="Saved today" value={summary.data?.savedToday ?? 0} note="by you" loading={summary.isLoading} />
            </Col>
          </Row>
        )}

        <Row gutter={[24, 24]}>
          <Col xs={24} xl={11}>
            <Card style={{ height: '100%' }} title={<CardTitle title="Where did you meet them?" subtitle="Goes with every card you scan or upload next. You can change it on each card when you check it." />}>
              <EventPicker value={eventTagId} onChange={chooseEvent} size="middle" />
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13, margin: '8px 0 20px' }}>
                {eventTagId === NO_EVENT ? 'No event is recorded. Good for a mixed pile.' : 'Saved contacts get this tag, and a note saying where you met them.'}
              </Typography.Text>
              <CardAudienceFields value={audience} onChange={chooseAudience} />
              {addedBy.data && (
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>
                  Saved cards are also tagged <Tag style={{ marginInlineEnd: 0 }}>{addedBy.data.name}</Tag>.
                </Typography.Text>
              )}
            </Card>
          </Col>
          <Col xs={24} xl={13}>
            <Card style={{ height: '100%' }} title={<CardTitle title="Scan" subtitle="Lay the card flat in good light and fill the frame. Keep scanning while earlier cards are read." />}>
              {pending ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <img src={pending.frontUrl} alt="Front of the card" style={{ width: 120, borderRadius: 8, border: `1px solid ${palette.border}` }} />
                    <Typography.Text>
                      <CheckCircleOutlined style={{ color: palette.success }} /> Front taken{pending.qrText ? ', QR code found' : ''}. Does the back have details too?
                    </Typography.Text>
                  </div>
                  <Button size="large" block icon={<CameraOutlined />} onClick={() => backInput.current?.click()} disabled={preparing}>
                    Add the back
                  </Button>
                  <Button size="large" type="primary" block icon={<SendOutlined />} onClick={() => void finish()} loading={preparing}>
                    No back, send it
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <Button type="primary" size="large" block icon={<CameraOutlined />} style={{ height: 52 }} loading={preparing} disabled={!!sending} onClick={() => frontInput.current?.click()}>
                    Scan a card
                  </Button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <Switch
                      size="small"
                      checked={frontOnly}
                      onChange={(on) => {
                        setFrontOnly(on);
                        writeFlag(FRONT_ONLY_KEY, on);
                      }}
                    />
                    <Typography.Text>Front only, for a pile of cards: send each photo straight away without asking for the back</Typography.Text>
                  </label>
                  <Upload.Dragger
                    accept="image/*"
                    multiple
                    showUploadList={false}
                    disabled={busy}
                    beforeUpload={(file, list) => {
                      // called once per file; start once, with the whole selection
                      if (file === list[0]) void onPhotos(list as File[]);
                      return false;
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">{phone ? 'Upload photos you already took' : 'Drop card photos here, or click to choose'}</p>
                    <p className="ant-upload-hint">Upload as many as you like at once.</p>
                  </Upload.Dragger>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Typography.Text type="secondary">Uploaded photos are</Typography.Text>
                    <Segmented<'single' | 'pairs'>
                      value={pairs ? 'pairs' : 'single'}
                      onChange={(v) => setPairs(v === 'pairs')}
                      disabled={busy}
                      options={[
                        { value: 'single', label: 'One photo per card' },
                        { value: 'pairs', label: 'Front and back pairs' },
                      ]}
                    />
                  </div>
                  {sending && (
                    <div>
                      <Typography.Text type="secondary">
                        Sending {sending.done} of {plural(sending.total, 'card')}…
                      </Typography.Text>
                      <Progress percent={Math.round((sending.done / sending.total) * 100)} status="active" />
                    </div>
                  )}
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Card title={<CardTitle title="Cards" subtitle="Check each card and save it, or save the ones read clearly in one go." />}>
          <div className="table-toolbar">
            <div className="toolbar-group">
              <Segmented<View>
                value={view}
                onChange={(v) => {
                  setView(v);
                  setSelected([]);
                }}
                options={[
                  { value: 'open', label: `To check${all.length ? ` (${all.length})` : ''}` },
                  { value: 'saved', label: 'Saved' },
                ]}
              />
            </div>
            {view === 'open' && (
              <div className="toolbar-group">
                {selectedReady.length > 0 ? (
                  <>
                    <Button danger icon={<DeleteOutlined />} onClick={() => runDiscard(selectedReady)}>
                      Discard {selectedReady.length}
                    </Button>
                    <Button type="primary" icon={<CheckOutlined />} loading={saveMany.isPending} onClick={() => runSaveMany(selectedReady)}>
                      Save {plural(selectedReady.length, 'selected card')}
                    </Button>
                  </>
                ) : (
                  <>
                    {phone && ready.length > 0 && (
                      <Button icon={<RightOutlined />} onClick={() => navigate(`${SCAN_PATH}/${ready[0].id}`)}>
                        Check one by one
                      </Button>
                    )}
                    <Button type="primary" icon={<CheckOutlined />} disabled={!ready.length} loading={saveMany.isPending} onClick={() => runSaveMany(ready)}>
                      Save all {ready.length ? plural(ready.length, 'read card') : 'read cards'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          {view === 'open' && reading.length > 0 && (
            <Alert type="info" showIcon icon={<SyncOutlined spin />} title={`Reading ${plural(reading.length, 'card')}…`} style={{ marginBottom: 16 }} />
          )}
          <Table<CardScanItem>
            rowKey="id"
            dataSource={rows}
            loading={tableLoading(view === 'saved' ? saved : scans)}
            locale={tableLocale(view === 'saved' ? saved : scans, view === 'saved' ? 'No cards saved yet.' : 'No cards waiting. Scanned cards show up here while they are read.')}
            rowSelection={view === 'open' ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), getCheckboxProps: (s) => ({ disabled: s.status !== 'READY' }) } : undefined}
            pagination={rows.length > 25 ? { defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'card') } : false}
            onRow={(s) => ({ onClick: (e) => !(e.target as HTMLElement).closest('button, a, .ant-checkbox-wrapper, .ant-dropdown') && s.status !== 'PROCESSING' && navigate(goTo(s)), style: { cursor: s.status === 'PROCESSING' ? undefined : 'pointer' } })}
            columns={[
              {
                title: 'Card',
                key: 'card',
                render: (_, s) => (
                  <NameCell
                    name={s.status === 'PROCESSING' ? 'Reading the card…' : s.status === 'FAILED' && !s.extracted ? 'Couldn’t read this card' : cardName(s)}
                    sub={s.status === 'FAILED' ? <Typography.Text type="danger" style={{ fontSize: 12 }}>{s.error ?? 'Unknown error'}</Typography.Text> : [s.extracted?.jobTitle, s.extracted?.company].filter(Boolean).join(' · ') || undefined}
                    image={fileUrl(s.frontFileId)}
                    square
                    size={44}
                    to={s.status === 'PROCESSING' ? undefined : goTo(s)}
                  />
                ),
              },
              { title: 'Status', key: 'status', width: 130, render: (_, s) => <StatusTag s={s} /> },
              {
                title: 'Met at',
                key: 'event',
                width: 180,
                responsive: ['md'],
                render: (_, s) => (s.eventTag ? <Tag>{s.eventTag.name}</Tag> : <Typography.Text type="secondary">Not recorded</Typography.Text>),
              },
              {
                title: 'Adds to',
                key: 'audience',
                responsive: ['lg'],
                render: (_, s) => {
                  const ids = [...s.tagIds, ...s.listIds, ...s.segmentIds].filter((id) => names.has(id));
                  return ids.length ? (
                    <Space size={4} wrap>
                      {ids.map((id) => (
                        <Tag key={id} color={s.tagIds.includes(id) ? 'blue' : s.listIds.includes(id) ? 'purple' : 'cyan'} style={{ marginInlineEnd: 0 }}>
                          {names.get(id)}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                  );
                },
              },
              { title: view === 'saved' ? 'Saved' : 'Scanned', key: 'when', width: 170, responsive: ['md'], render: (_, s) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(view === 'saved' ? s.updatedAt : s.createdAt)}</span> },
              {
                key: 'actions',
                width: 48,
                align: 'right',
                render: (_, s) => {
                  const items =
                    s.status === 'SAVED'
                      ? [{ key: 'open', icon: <UserOutlined />, label: 'Open contact', disabled: !s.contactId, onClick: () => navigate(goTo(s)) }]
                      : [
                          { key: 'check', icon: <ScanOutlined />, label: 'Check card', disabled: s.status === 'PROCESSING', onClick: () => navigate(goTo(s)) },
                          { key: 'retry', icon: <ReloadOutlined />, label: 'Read again', disabled: s.status === 'PROCESSING', onClick: () => retry.mutate(s.id, { onError: (e) => message.error(errorMessage(e)) }) },
                          { type: 'divider' as const },
                          { key: 'discard', icon: <DeleteOutlined />, label: 'Discard', danger: true, onClick: () => runDiscard([s]) },
                        ];
                  return (
                    <Dropdown trigger={['click']} menu={{ items }}>
                      <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Card actions" loading={(retry.isPending && retry.variables === s.id) || (discard.isPending && discard.variables === s.id)} />
                    </Dropdown>
                  );
                },
              },
            ]}
          />
        </Card>
      </Space>
    </div>
  );
}
